import { Inject, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, tap } from 'rxjs';
import { API_BASE } from '../../ordering/data/ordering-api';
import { DevicePushService, PushSurface } from '../../../Shared/device-push.service';
import { ApiResponse, RiderAuthResult, RiderDocument, RiderProfile, RiderRegisterRequest } from './rider.models';

const TOKEN_KEY = 'orderyaa.rider.access';
const REFRESH_KEY = 'orderyaa.rider.refresh';

/** Web-push wiring for the rider audience (Rider scheme → /api/rider/device/push-token). */
const RIDER_PUSH: PushSurface = {
  key: 'rider',
  registerUrl: `${API_BASE}/api/rider/device/push-token`,
  unregisterUrl: `${API_BASE}/api/rider/device/push-token`,
};

/**
 * Rider portal auth — email + password against /api/rider-auth, signal-based session state.
 * Tokens persist to localStorage (browser only); riderAuthInterceptor attaches the access token and
 * silently refreshes it (12-hour access tokens, 30-day rotating refresh tokens) so long shifts and
 * overnight tabs don't strand a rider on a dead session.
 */
@Injectable({ providedIn: 'root' })
export class RiderAuthService {
  private readonly base = `${API_BASE}/api/rider-auth`;
  private readonly http = inject(HttpClient);
  private readonly push = inject(DevicePushService);
  private readonly isBrowser: boolean;

  private readonly _token = signal<string | null>(null);
  private readonly _refresh = signal<string | null>(null);
  private readonly _profile = signal<RiderProfile | null>(null);

  readonly isAuthenticated = computed(() => !!this._token());
  readonly profile = this._profile.asReadonly();

  /** Shared in-flight refresh so concurrent expired-token requests trigger ONE refresh call —
   *  the server rotates the refresh token, so parallel refreshes would kill each other's session. */
  private _refreshPromise: Promise<boolean> | null = null;

  /** Bumped by clearSession(); a refresh captures it and discards its result if it changed, so a
   *  refresh in flight at logout (e.g. the logout push-token DELETE proactively refreshing an expired
   *  token) can't RESURRECT the session after teardown. Mirrors CustomerAuthService. */
  private _sessionEpoch = 0;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this._token.set(localStorage.getItem(TOKEN_KEY));
      this._refresh.set(localStorage.getItem(REFRESH_KEY));
      // Returning signed-in rider: silently refresh the push token (no re-prompt) on app open.
      if (this._token()) void this.push.syncIfPermitted(RIDER_PUSH);
    }
  }

  get accessToken(): string | null {
    return this._token();
  }

  /** Sign-up email verification — send a one-time code (step 1 of account creation; fails early if the email is taken). */
  requestSignupOtp(email: string): Observable<ApiResponse<{ expiresInSeconds: number }>> {
    return this.http.post<ApiResponse<{ expiresInSeconds: number }>>(`${this.base}/otp/request`, { email });
  }

  /** Sign-up email verification — validate the emailed code only (no session; register() follows). */
  verifySignupOtp(email: string, code: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.base}/otp/verify`, { email, code });
  }

  /** Create the rider account — call after the sign-up code checked out. Signs the rider in. */
  register(body: RiderRegisterRequest): Observable<ApiResponse<RiderAuthResult>> {
    return this.http
      .post<ApiResponse<RiderAuthResult>>(`${this.base}/register`, body)
      .pipe(tap((res) => { if (res.success && res.data) { this.setSession(res.data); void this.push.register(RIDER_PUSH); } }));
  }

  login(email: string, password: string): Observable<ApiResponse<RiderAuthResult>> {
    return this.http
      .post<ApiResponse<RiderAuthResult>>(`${this.base}/login`, { email, password })
      .pipe(tap((res) => { if (res.success && res.data) { this.setSession(res.data); void this.push.register(RIDER_PUSH); } }));
  }

  /**
   * Silently exchange the refresh token for a fresh access token. Returns true on success, false
   * when there is no refresh token or the server rejects it. Concurrent callers share one request.
   */
  refreshAccessToken(): Promise<boolean> {
    if (this._refreshPromise) return this._refreshPromise;

    const refreshToken = this._refresh();
    if (!refreshToken) return Promise.resolve(false);

    const epoch = this._sessionEpoch; // if the session is torn down before we resolve, don't resurrect it
    this._refreshPromise = firstValueFrom(
      this.http.post<ApiResponse<RiderAuthResult>>(`${this.base}/refresh`, { refreshToken }),
    )
      .then((res) => {
        if (epoch !== this._sessionEpoch) return false; // logged out meanwhile — discard this refresh
        if (res?.success && res.data?.accessToken) {
          this.setSession(res.data); // refresh returns the full profile too — safe to re-apply
          return true;
        }
        return false;
      })
      .catch(() => false)
      .finally(() => { this._refreshPromise = null; });

    return this._refreshPromise;
  }

  // ── Token expiry (mirrors the partner/customer auth services) ────────────────

  private decodeToken(token: string): { exp?: number } {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT');
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  }

  /** True when there is no token, or the current access token's exp has passed. */
  isTokenExpired(): boolean {
    const token = this._token();
    if (!token) return true;
    try {
      const p = this.decodeToken(token);
      if (!p?.exp) return false; // no exp claim → treat as non-expiring
      return Date.now() >= p.exp * 1000;
    } catch {
      return true; // malformed token = expired
    }
  }

  /** Resolves true when a usable session is in place — live token, or expired-but-refreshable. */
  async ensureAuthenticated(): Promise<boolean> {
    if (!this._token()) return false;
    if (!this.isTokenExpired()) return true;
    return this.refreshAccessToken();
  }

  /** Request a password-reset code by email (always succeeds; doesn't reveal if the email exists). */
  requestPasswordReset(email: string): Observable<ApiResponse<{ expiresInSeconds: number }>> {
    return this.http.post<ApiResponse<{ expiresInSeconds: number }>>(`${this.base}/forgot-password/request`, { email });
  }

  /** Verify the emailed reset code and set a new password. */
  resetPassword(email: string, code: string, newPassword: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.base}/forgot-password/verify`, { email, code, newPassword });
  }

  loadProfile(): Observable<ApiResponse<RiderProfile>> {
    return this.http
      .get<ApiResponse<RiderProfile>>(`${this.base}/me`)
      .pipe(tap((res) => res.success && this._profile.set(res.data)));
  }

  getDocuments(): Observable<ApiResponse<RiderDocument[]>> {
    return this.http.get<ApiResponse<RiderDocument[]>>(`${this.base}/documents`);
  }

  uploadDocument(docType: string, file: File): Observable<ApiResponse<RiderDocument>> {
    const fd = new FormData();
    fd.append('docType', docType);
    fd.append('file', file);
    return this.http.post<ApiResponse<RiderDocument>>(`${this.base}/documents`, fd);
  }

  /** User-initiated sign-out: revoke the refresh token server-side (best-effort), then clear. */
  logout(): void {
    const refresh = this._refresh();
    // Clear the device row server-side FIRST — the interceptor still has our live JWT here.
    void this.push.unregister(RIDER_PUSH);
    this.clearSession();
    if (this.isBrowser && refresh) {
      this.http.post(`${this.base}/logout`, { refreshToken: refresh }).subscribe({ error: () => {} });
    }
  }

  /** Local teardown only (no server round-trip) — used when the session is already dead. */
  clearSession(): void {
    this._sessionEpoch++; // invalidate any in-flight refresh so it can't repopulate tokens after teardown
    this._token.set(null);
    this._refresh.set(null);
    this._profile.set(null);
    if (this.isBrowser) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      this.push.clearLocal(RIDER_PUSH); // forget the cached push token so the next sign-in re-registers
    }
  }

  private setSession(d: RiderAuthResult): void {
    this._token.set(d.accessToken);
    if (d.refreshToken) this._refresh.set(d.refreshToken);
    this._profile.set(d.profile);
    if (this.isBrowser) {
      localStorage.setItem(TOKEN_KEY, d.accessToken);
      if (d.refreshToken) localStorage.setItem(REFRESH_KEY, d.refreshToken);
    }
    // NB: push registration is triggered from login()/register() only — NOT here — because setSession
    // is also called on silent refresh, and we must not surprise-prompt a returning rider on refresh.
  }
}
